import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { useMakes } from "@/hooks/useMakes";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { getTrimOptionsForMake } from "@/constants/trimByMake";
import {
  resolveEngineOnCatalogLoad,
  shouldExposeEngineOtherInput,
} from "@/utils/engineMatch";

interface DetailsSectionProps {
  fuelType: string | undefined;
  engine: string | undefined;
  transmission: string | undefined;
  bodyType: string | undefined;
  color: string | undefined;
  variant?: string;
  onSelect: (
    value: string,
    field: "fuelType" | "engine" | "transmission" | "bodyType" | "color",
  ) => void;
  onVariantChange?: (variant: string) => void;
  trim?: string;
  onTrimChange?: (trim: string) => void;
  selectedMake?: string;
  isAutoLoaded?: boolean;
  vehicleType?: "car" | "mc";
  onAutoLoadedFieldEditAttempt?: () => void;
}

const MOCK_FUEL_TYPES = [
  "Bensin",
  "Diesel",
  "El",
  "Hybrid",
  "Laddhybrid",
  "Gas",
  "Etanol",
  "Annat",
];
const MOCK_BODY_TYPES = [
  "Sedan",
  "Kombi",
  "SUV",
  "Halvkombi",
  "Coupe",
  "Cabriolet",
  "Minibuss",
  "Pickup",
];
const MOCK_TRANSMISSIONS = ["Automat", "Manuell", "Halvautomatisk", "CVT"];
const MOCK_COLORS = [
  "Svart",
  "Vit",
  "Silver",
  "Blå",
  "Röd",
  "Grå",
  "Grön",
  "Brun",
  "Guld",
  "Beige",
];

const ADDITIONAL_COLORS = [
  "Lila",
  "Orange",
  "Rosa",
  "Turkos",
  "Burgundy",
  "Koppar",
  "Grön metallisk",
  "Blå metallisk",
  "Röd metallisk",
  "Svart metallisk",
  "Vit metallisk",
  "Silver metallisk",
];

export const DetailsSection = ({
  fuelType,
  engine,
  transmission,
  bodyType,
  color,
  variant,
  onSelect,
  onVariantChange,
  trim,
  onTrimChange,
  selectedMake,
  isAutoLoaded = false,
  vehicleType,
  onAutoLoadedFieldEditAttempt,
}: DetailsSectionProps) => {
  const [showAllFuelTypes, setShowAllFuelTypes] = useState(false);
  const [showAllEngines, setShowAllEngines] = useState(false);
  const [showAllBodyTypes, setShowAllBodyTypes] = useState(false);
  const [showAllTransmissions, setShowAllTransmissions] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);
  const [showEngineOtherInput, setShowEngineOtherInput] = useState(false);
  const [engineOtherText, setEngineOtherText] = useState("");
  const [showBodyTypeOtherInput, setShowBodyTypeOtherInput] = useState(false);
  const [bodyTypeOtherText, setBodyTypeOtherText] = useState("");
  const [showColorOtherInput, setShowColorOtherInput] = useState(false);
  const [colorOtherText, setColorOtherText] = useState("");
  const [showTrimOtherInput, setShowTrimOtherInput] = useState(false);
  const [trimOtherText, setTrimOtherText] = useState("");

  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;
  const opts =
    (vLabels as { options?: Record<string, Record<string, string>> }).options ??
    {};

  const getOptionLabel = (
    category: "fuelType" | "bodyType" | "transmission" | "color",
    svKey: string,
  ) => opts[category]?.[svKey] ?? svKey;

  // Always load makes data when we have a selectedMake (needed for engine options)
  // Even if auto-loaded, we need to reload makes when user changes make
  const { makes, isLoadingMakes } = useMakes(true);

  // Get selected make object
  const selectedMakeObj = useMemo(() => {
    if (!selectedMake || !makes.length) return null;
    return makes.find((m) => m.name === selectedMake);
  }, [makes, selectedMake]);

  // Reset engine when make changes - always reset when selectedMake changes
  const prevSelectedMakeRef = useRef<string | undefined>(selectedMake);
  const engineResolvedRef = useRef(false);
  useEffect(() => {
    // Only reset if selectedMake actually changed, not on initial load
    if (
      !prevSelectedMakeRef.current ||
      prevSelectedMakeRef.current === selectedMake
    ) {
      prevSelectedMakeRef.current = selectedMake;
      return;
    }

    const prevMake = prevSelectedMakeRef.current;
    prevSelectedMakeRef.current = selectedMake;

    // If make changed and we have an engine, reset it.
    // Engine options depend on the make, so a leftover engine from the previous make is
    // always wrong.
    //
    // This deliberately fires even when isAutoLoaded. Before engine prefill existed the
    // engine field was always empty after a lookup, so the isAutoLoaded guard was inert.
    // Now that a lookup populates it, a dealer correcting a wrongly-detected make would
    // otherwise keep the PREVIOUS make's engine string - a wrong-data path that did not
    // exist before. The change is dealer-initiated by construction: this branch only runs
    // when selectedMake actually changed from one non-empty value to another.
    if (engine && prevMake && selectedMake && prevMake !== selectedMake) {
      onSelect("", "engine");
      setShowEngineOtherInput(false);
      setEngineOtherText("");
      // Allow the catalog-load effect to resolve an engine for the newly chosen make.
      engineResolvedRef.current = false;
    }
  }, [selectedMake, engine, isAutoLoaded, onSelect]);


  // Get engines for selected make (excluding "Övrigt")
  const availableEngines = useMemo(() => {
    if (!selectedMakeObj) return [];
    return selectedMakeObj.engines.filter((e) => e.name !== "Övrigt");
  }, [selectedMakeObj]);

  // Upgrade the raw looked-up engine string to a canonical catalog chip once the makes
  // catalog has loaded. See resolveEngineOnCatalogLoad for the ordering guarantees; the
  // ref makes it one-shot so it can never clobber a dealer's later edit.
  useEffect(() => {
    const chips = availableEngines.map((e) =>
      typeof e === "string" ? e : e.name,
    );

    const { shouldSet, value } = resolveEngineOnCatalogLoad({
      currentEngine: engine,
      chips,
      isAutoLoaded: Boolean(isAutoLoaded),
      alreadyResolved: engineResolvedRef.current,
    });

    if (shouldSet && value) {
      engineResolvedRef.current = true;
      onSelect(value, "engine");
    }
  }, [availableEngines, engine, isAutoLoaded, onSelect]);

  // Auto-expand the "Övrigt" free-text box when the engine value matches no chip, so a
  // prefilled value is visible and verifiable at a glance instead of hiding behind a click.
  //
  // Keyed on the engine value rather than run unconditionally: after the dealer submits
  // the free-text box it closes, and without this guard the effect would immediately
  // reopen it on the very same value and fight them.
  const autoExpandedForEngineRef = useRef<string | null>(null);
  useEffect(() => {
    const chips = availableEngines.map((e) =>
      typeof e === "string" ? e : e.name,
    );

    const expose = shouldExposeEngineOtherInput({
      engine,
      chips,
      catalogLoaded: !isLoadingMakes,
    });

    if (expose && autoExpandedForEngineRef.current !== engine) {
      autoExpandedForEngineRef.current = engine ?? null;
      setShowEngineOtherInput(true);
      setEngineOtherText(engine ?? "");
    } else if (!expose && !engine) {
      // Engine cleared (e.g. make changed) - allow a future value to auto-expand again.
      autoExpandedForEngineRef.current = null;
    }
  }, [availableEngines, engine, isLoadingMakes]);

  // Display engines: for 4 columns, show 7 engines + Övrigt = 8 items (2 rows)
  const displayedEngines = useMemo(() => {
    // Only show engines from API - no mock fallback
    if (availableEngines.length > 0) {
      if (showAllEngines) {
        return availableEngines;
      }
      // Show 7 engines (to fill 2 rows of 4 columns, with Övrigt as 8th item)
      return availableEngines.slice(0, 7);
    }
    // If no engines from API, return empty array - only "Övrigt" will be shown
    return [];
  }, [availableEngines, showAllEngines]);

  const handleEngineOtherClick = () => {
    setShowEngineOtherInput(true);
    // If we already have an engine value (e.g., from AutoUncle), pre-fill it
    if (engine && !engineOtherText) {
      setEngineOtherText(engine);
    } else {
      onSelect("", "engine");
    }
  };

  const handleEngineOtherSubmit = () => {
    if (engineOtherText.trim()) {
      onSelect(engineOtherText.trim(), "engine");
      setShowEngineOtherInput(false);
    }
  };

  // Check if bodyType is not in the standard list - if so, show it in "Övrigt" input
  useEffect(() => {
    if (bodyType && !MOCK_BODY_TYPES.includes(bodyType)) {
      setShowBodyTypeOtherInput(true);
      setBodyTypeOtherText(bodyType);
    }
  }, [bodyType]);

  const handleBodyTypeOtherClick = () => {
    setShowBodyTypeOtherInput(true);
    // If we already have a bodyType value (e.g., from AutoUncle), pre-fill it
    if (bodyType && !bodyTypeOtherText) {
      setBodyTypeOtherText(bodyType);
    } else {
      onSelect("", "bodyType");
    }
  };

  const handleBodyTypeOtherSubmit = () => {
    if (bodyTypeOtherText.trim()) {
      onSelect(bodyTypeOtherText.trim(), "bodyType");
      setShowBodyTypeOtherInput(false);
    }
  };

  // Check if color is not in the standard list - if so, show it in "Övrigt" input
  useEffect(() => {
    if (
      color &&
      !MOCK_COLORS.includes(color) &&
      !ADDITIONAL_COLORS.includes(color)
    ) {
      setShowColorOtherInput(true);
      setColorOtherText(color);
    }
  }, [color]);

  // Utrustningsnivå (trim): make-specific chips. When the current trim matches no chip,
  // the free-text box auto-expands with the value, mirroring the engine Övrigt pattern.
  const trimOptions = useMemo(
    () => getTrimOptionsForMake(selectedMake),
    [selectedMake]
  );
  const hasTrimOptions = trimOptions.length > 0;
  const trimIsInOptions =
    !!trim &&
    hasTrimOptions &&
    trimOptions.some((o) => o === trim);

  useEffect(() => {
    if (!hasTrimOptions) return;
    if (trim && trimOptions.includes(trim)) {
      setShowTrimOtherInput(false);
      setTrimOtherText("");
      return;
    }
    if (trim && !trimOptions.includes(trim)) {
      setShowTrimOtherInput(true);
      setTrimOtherText(trim);
    }
  }, [hasTrimOptions, trim, trimOptions]);

  // Display order: current trim first if it matches a chip, then the rest, then Övrigt
  const orderedTrimOptions = useMemo(() => {
    if (!hasTrimOptions) return [];
    const rest = trimOptions.filter((o) => o !== trim);
    if (trim && trimOptions.includes(trim)) {
      return [trim, ...rest];
    }
    return [...trimOptions];
  }, [trimOptions, trim, hasTrimOptions]);

  const handleTrimOptionSelect = (value: string) => {
    if (value === "__other__") {
      setShowTrimOtherInput(true);
      if (trim && !trimOptions.includes(trim)) {
        setTrimOtherText(trim);
      } else {
        setTrimOtherText("");
      }
      onTrimChange?.("");
      return;
    }
    setShowTrimOtherInput(false);
    setTrimOtherText("");
    onTrimChange?.(value);
  };

  const handleTrimOtherSubmit = () => {
    if (trimOtherText.trim()) {
      onTrimChange?.(trimOtherText.trim());
    }
  };

  const handleColorOtherClick = () => {
    setShowColorOtherInput(true);
    // If we already have a color value (e.g., from AutoUncle), pre-fill it
    if (color && !colorOtherText) {
      setColorOtherText(color);
    } else {
      onSelect("", "color");
    }
  };

  const handleColorOtherSubmit = () => {
    if (colorOtherText.trim()) {
      onSelect(colorOtherText.trim(), "color");
      setShowColorOtherInput(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="font-medium text-sm">{vLabels.fuelType}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {(showAllFuelTypes
            ? MOCK_FUEL_TYPES
            : MOCK_FUEL_TYPES.slice(0, 8)
          ).map((type) => (
            <Button
              key={type}
              onClick={() => {
                if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
                  onAutoLoadedFieldEditAttempt();
                  return;
                }
                onSelect(type, "fuelType");
              }}
              variant="formOption"
              className={`${
                fuelType === type
                  ? "border-accent border-2 bg-accent/20"
                  : "border-gray-400"
              } truncate`}
              title={getOptionLabel("fuelType", type)}
            >
              {getOptionLabel("fuelType", type)}
            </Button>
          ))}
        </div>
        {MOCK_FUEL_TYPES.length > 8 && (
          <Button
            variant="ghost"
            className="w-full mt-1 text-sm flex items-center justify-center gap-2 pt-2"
            onClick={() => setShowAllFuelTypes(!showAllFuelTypes)}
          >
            {showAllFuelTypes ? vLabels.showLess : vLabels.showMore}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                showAllFuelTypes ? "rotate-180" : ""
              }`}
            />
          </Button>
        )}
      </div>

      {vehicleType !== "mc" && (
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{vLabels.bodyType}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {(showAllBodyTypes
              ? MOCK_BODY_TYPES
              : MOCK_BODY_TYPES.slice(0, 8)
            ).map((type) => (
              <Button
                key={type}
                onClick={() => {
                  if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
                    onAutoLoadedFieldEditAttempt();
                    return;
                  }
                  onSelect(type, "bodyType");
                  setShowBodyTypeOtherInput(false);
                  setBodyTypeOtherText("");
                }}
                variant="formOption"
                className={`${
                  bodyType === type
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                } truncate`}
                title={getOptionLabel("bodyType", type)}
              >
                {getOptionLabel("bodyType", type)}
              </Button>
            ))}
            {/* Övrigt option */}
            {!showBodyTypeOtherInput && (
              <Button
                variant="formOption"
                onClick={handleBodyTypeOtherClick}
                className={
                  bodyType && !MOCK_BODY_TYPES.includes(bodyType)
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                }
              >
                {vLabels.other}
              </Button>
            )}
          </div>
          {showBodyTypeOtherInput && (
            <div className="flex gap-2">
              <Input
                value={bodyTypeOtherText}
                onChange={(e) => setBodyTypeOtherText(e.target.value)}
                placeholder={vLabels.enterBodyType}
                maxLength={30}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleBodyTypeOtherSubmit();
                  }
                }}
              />
              <Button
                onClick={handleBodyTypeOtherSubmit}
                disabled={!bodyTypeOtherText.trim()}
              >
                {vLabels.save}
              </Button>
            </div>
          )}
          {MOCK_BODY_TYPES.length > 8 && (
            <Button
              variant="ghost"
              className="w-full mt-1 text-sm flex items-center justify-center gap-2 pt-2"
              onClick={() => setShowAllBodyTypes(!showAllBodyTypes)}
            >
              {showAllBodyTypes ? vLabels.showLess : vLabels.showMore}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  showAllBodyTypes ? "rotate-180" : ""
                }`}
              />
            </Button>
          )}
        </div>
      )}

      {(bodyType || vehicleType === "mc") && (
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{vLabels.transmission}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {(showAllTransmissions
              ? MOCK_TRANSMISSIONS
              : MOCK_TRANSMISSIONS.slice(0, 4)
            ).map((type) => (
              <Button
                key={type}
                onClick={() => {
                  if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
                    onAutoLoadedFieldEditAttempt();
                    return;
                  }
                  onSelect(type, "transmission");
                }}
                variant="formOption"
                className={`${
                  transmission === type
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                } truncate`}
                title={getOptionLabel("transmission", type)}
              >
                {getOptionLabel("transmission", type)}
              </Button>
            ))}
          </div>
          {MOCK_TRANSMISSIONS.length > 4 && (
            <Button
              variant="ghost"
              className="w-full mt-1 text-sm flex items-center justify-center gap-2 pt-2"
              onClick={() => setShowAllTransmissions(!showAllTransmissions)}
            >
              {showAllTransmissions ? vLabels.showLess : vLabels.showMore}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  showAllTransmissions ? "rotate-180" : ""
                }`}
              />
            </Button>
          )}
        </div>
      )}

      {transmission && (
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{vLabels.color}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
            {(() => {
              const allColors = showAllColors
                ? [...MOCK_COLORS, ...ADDITIONAL_COLORS]
                : MOCK_COLORS;
              return allColors.map((type) => (
                <Button
                  key={type}
                  onClick={() => {
                    // Color is NOT blocked because AutoUncle doesn't provide it
                    onSelect(type, "color");
                    setShowColorOtherInput(false);
                    setColorOtherText("");
                  }}
                  variant="formOption"
                  className={`${
                    color === type
                      ? "border-accent border-2 bg-accent/20"
                      : "border-gray-400"
                  } truncate`}
                  title={getOptionLabel("color", type)}
                >
                  {getOptionLabel("color", type)}
                </Button>
              ));
            })()}
            {/* Övrigt option */}
            {!showColorOtherInput && (
              <Button
                variant="formOption"
                onClick={handleColorOtherClick}
                className={
                  color &&
                  !MOCK_COLORS.includes(color) &&
                  !ADDITIONAL_COLORS.includes(color)
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                }
              >
                {vLabels.other}
              </Button>
            )}
          </div>
          {showColorOtherInput && (
            <div className="flex gap-2">
              <Input
                value={colorOtherText}
                onChange={(e) => setColorOtherText(e.target.value)}
                placeholder={vLabels.enterColor}
                maxLength={30}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleColorOtherSubmit();
                  }
                }}
              />
              <Button
                onClick={handleColorOtherSubmit}
                disabled={!colorOtherText.trim()}
              >
                {vLabels.save}
              </Button>
            </div>
          )}
          <Button
            variant="ghost"
            className="w-full mt-1 text-sm flex items-center justify-center gap-2 pt-2"
            onClick={() => setShowAllColors(!showAllColors)}
          >
            {showAllColors ? vLabels.showLess : vLabels.more}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                showAllColors ? "rotate-180" : ""
              }`}
            />
          </Button>
        </div>
      )}

      {/* Motor moved to the end, after Färg - only show after all other fields are answered */}
      {fuelType &&
        fuelType !== "El" &&
        fuelType !== "Elbil" &&
        fuelType !== "ELECTRIC" &&
        (vehicleType === "mc" || bodyType) &&
        transmission &&
        color && (
          <div className="space-y-1">
            <h3 className="font-medium text-sm">{vLabels.engine}</h3>
            {isLoadingMakes ? (
              <div className="text-sm text-muted-foreground">
                {vLabels.loadingEngines}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {displayedEngines.map((engineItem) => {
                    const engineName =
                      typeof engineItem === "string"
                        ? engineItem
                        : engineItem.name;
                    const engineId =
                      typeof engineItem === "string"
                        ? engineItem
                        : engineItem.id;
                    return (
                      <Button
                        key={engineId}
                        onClick={() => {
                          if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
                            onAutoLoadedFieldEditAttempt();
                            return;
                          }
                          onSelect(engineName, "engine");
                        }}
                        variant="formOption"
                        className={`${
                          engine === engineName
                            ? "border-accent border-2 bg-accent/20"
                            : "border-gray-400"
                        } whitespace-normal break-words min-h-[2.5rem] py-2 text-center leading-tight`}
                        title={engineName}
                      >
                        {engineName}
                      </Button>
                    );
                  })}
                  {/* Övrigt option */}
                  {!showEngineOtherInput && (
                    <Button
                      variant="formOption"
                      onClick={handleEngineOtherClick}
                      className={
                        engine &&
                        availableEngines.length > 0 &&
                        !availableEngines.some((e) => {
                          const engineName = typeof e === "string" ? e : e.name;
                          return engineName === engine;
                        })
                          ? "border-accent border-2 bg-accent/20"
                          : "border-gray-400"
                      }
                    >
                      {vLabels.other}
                    </Button>
                  )}
                </div>
                {showEngineOtherInput && (
                  <div className="flex gap-2">
                    <Input
                      value={engineOtherText}
                      onChange={(e) => setEngineOtherText(e.target.value)}
                      placeholder={vLabels.enterEngine}
                      maxLength={30}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleEngineOtherSubmit();
                        }
                      }}
                    />
                    <Button
                      onClick={handleEngineOtherSubmit}
                      disabled={!engineOtherText.trim()}
                    >
                      {vLabels.save}
                    </Button>
                  </div>
                )}
                {(availableEngines.length > 7 ||
                  (isAutoLoaded && displayedEngines.length > 7)) && (
                  <Button
                    variant="ghost"
                    className="w-full mt-1 text-sm flex items-center justify-center gap-2 pt-2"
                    onClick={() => setShowAllEngines(!showAllEngines)}
                  >
                    {showAllEngines ? vLabels.showLess : vLabels.showMore}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        showAllEngines ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                )}
              </>
            )}
          </div>
        )}

      {/* Variant Field - Shows after engine is set (or for electric vehicles where engine is not required) */}
      {(() => {
        const isElectric =
          fuelType === "El" || fuelType === "Elbil" || fuelType === "ELECTRIC";
        const hasEngine = isElectric ? true : !!engine;

        return (
          fuelType &&
          (vehicleType === "mc" || bodyType) &&
          transmission &&
          color &&
          hasEngine && (
            <>
              {/* Variant - the drivetrain descriptor ("Recharge T8 AWD", "1.6 TDI").
                  Free text: this is prose from the provider, not a fixed vocabulary. */}
              <div className="space-y-1.5 mb-5">
                <h3 className="font-medium text-sm">{vLabels.variant}</h3>
                <Input
                  type="text"
                  value={variant || ""}
                  onChange={(e) => onVariantChange?.(e.target.value)}
                  placeholder={
                    (vLabels as { variantFreeTextOnlyPlaceholder?: string })
                      .variantFreeTextOnlyPlaceholder ??
                    vLabels.variantPlaceholder
                  }
                  className="h-9"
                />
              </div>

              {/* Utrustningsnivå - the trim / equipment level ("M Sport", "Tekna").
                  Chips come from the per-make trim list and write to `trim`. */}
              <div className="space-y-1.5 mb-5">
                <h3 className="font-medium text-sm">{vLabels.equipmentLevel}</h3>
                {hasTrimOptions ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {orderedTrimOptions.map((opt) => (
                        <Button
                          key={opt}
                          variant="formOption"
                          onClick={() => handleTrimOptionSelect(opt)}
                          className={`truncate ${
                            trim === opt && !showTrimOtherInput
                              ? "border-accent border-2 bg-accent/20"
                              : "border-gray-400"
                          }`}
                          title={opt}
                        >
                          {opt}
                        </Button>
                      ))}
                      <Button
                        variant="formOption"
                        onClick={() => handleTrimOptionSelect("__other__")}
                        className={
                          showTrimOtherInput || (trim && !trimIsInOptions)
                            ? "border-accent border-2 bg-accent/20"
                            : "border-gray-400"
                        }
                      >
                        {vLabels.other}
                      </Button>
                    </div>
                    {showTrimOtherInput && (
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={trimOtherText}
                          onChange={(e) => setTrimOtherText(e.target.value)}
                          placeholder={vLabels.trimPlaceholder}
                          className="h-9 flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleTrimOtherSubmit();
                          }}
                        />
                        <Button
                          onClick={handleTrimOtherSubmit}
                          disabled={!trimOtherText.trim()}
                        >
                          {vLabels.save}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Input
                    type="text"
                    value={trim || ""}
                    onChange={(e) => onTrimChange?.(e.target.value)}
                    placeholder={vLabels.trimPlaceholder}
                    className="h-9"
                  />
                )}
              </div>
            </>
          )
        );
      })()}
    </div>
  );
};
